import { useState, useEffect, useContext } from 'react'
import { Row } from '@tanstack/react-table'
import { ListViewContext } from '../contexts/ListViewContext'

interface UseListViewSelectionProps<T extends { guid: string }> {
  checkedRows?: string[] | undefined
  setCheckedRows?: (connectedItems: string[]) => void
  pageIndex: number
  rows?: Row<T>[]
  dataLength?: number
  pageSize?: number
}

export function useListViewSelection<T extends { guid: string }>({
  checkedRows,
  setCheckedRows,
  pageIndex,
  rows: _rows,
  dataLength,
  pageSize,
}: UseListViewSelectionProps<T>) {
  const { showOnlySelected } = useContext(ListViewContext)

  const [selected, setSelected] = useState<readonly string[]>([])
  const [selectedByPage, setSelectedByPage] = useState<Set<string>[]>([])
  const [selectedAllPages, setSelectedAllPages] = useState<Set<string>>(
    new Set(),
  )
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null,
  )

  // Sync selected with setCheckedRows if provided
  useEffect(() => {
    if (selected && setCheckedRows) {
      setCheckedRows([...selected])
    }
  }, [selected, setCheckedRows])

  // Sync checkedRows with selectedAllPages
  useEffect(() => {
    const newSelectedAllPages = new Set(checkedRows)

    // Check if checkedRows and selectedAllPages are different using proper set equality
    if (
      newSelectedAllPages.size !== selectedAllPages.size ||
      [...newSelectedAllPages].some((value) => !selectedAllPages.has(value))
    ) {
      setSelectedAllPages(newSelectedAllPages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedRows])

  // Update selected when selectedAllPages changes
  useEffect(() => {
    const newSelected = Array.from(selectedAllPages)
    setSelected(newSelected)
  }, [selectedAllPages])

  const handleClick = (
    event: React.MouseEvent<unknown>,
    row: Row<T>,
    index: number,
    currentRows: Row<T>[],
  ) => {
    const clickedEditButton = (event.target as HTMLElement).closest(
      '.edit-button',
    )

    if (!clickedEditButton) {
      const newSelectedAllPages = new Set(selectedAllPages)
      let isSelecting = true

      // Keep track of the current index
      let currentIndex = lastSelectedIndex

      if (selectedAllPages.has(row.original.guid)) {
        newSelectedAllPages.delete(row.original.guid)
        isSelecting = false
      } else {
        newSelectedAllPages.add(row.original.guid)
      }

      if (isSelecting) {
        // Update currentIndex only when selecting
        currentIndex = index
      } else {
        // Clear currentIndex when deselecting
        currentIndex = null
      }

      // Check if shift was pressed, then select all between lastSelectedIndex and currentIndex
      if (
        event.shiftKey &&
        currentIndex !== null &&
        lastSelectedIndex !== null
      ) {
        if (lastSelectedIndex > currentIndex) {
          const start = currentIndex
          const end = lastSelectedIndex
          for (let i = start + 1; i <= end; i++) {
            if (currentRows[i]) {
              newSelectedAllPages.add(currentRows[i].original.guid)
            }
          }
        } else {
          const start = lastSelectedIndex
          const end = currentIndex
          for (let i = start; i <= end; i++) {
            if (currentRows[i]) {
              newSelectedAllPages.add(currentRows[i].original.guid)
            }
          }
        }
      }

      // Set the selected state with all lines selected
      setSelectedAllPages(newSelectedAllPages)
      // Update lastSelectedIndex with currentIndex
      setLastSelectedIndex(currentIndex)
      //Update the selected state with items from selectedAllPages
      const newSelected = Array.from(newSelectedAllPages)
      setSelected(newSelected)
    }
  }

  const handleSelectAllClick = (currentRows: Row<T>[]) => {
    // Ensure selectedByPage has enough pages
    const newSelectedByPage = [...selectedByPage]
    while (newSelectedByPage.length <= pageIndex) {
      newSelectedByPage.push(new Set<string>())
    }

    const currentPageSelection =
      newSelectedByPage[pageIndex] || new Set<string>()
    const allItemsOnPage = currentRows.map((n) => n.original.guid)
    const allSelectedPages = new Set(selectedAllPages)

    if (!showOnlySelected) {
      // Toggle the selection status for all items on the current page
      allItemsOnPage.forEach((item) => {
        if (currentPageSelection.has(item)) {
          currentPageSelection.delete(item)
          allSelectedPages.delete(item) // Deselect from all pages
        } else {
          currentPageSelection.add(item)
          allSelectedPages.add(item) // Select on all pages
        }
      })

      // Update the selectedByPage state for the current page
      newSelectedByPage[pageIndex] = new Set(currentPageSelection)
      setSelectedByPage(newSelectedByPage)

      // Update the selected state with items from selectedAllPages
      setSelectedAllPages(allSelectedPages)
      const newSelected = Array.from(allSelectedPages)
      setSelected(newSelected)
    }
  }

  const checkboxStateIndeterminate = (rows: Row<T>[]) => {
    const selectedCount = rows.filter((r) =>
      selectedAllPages.has(r.original.guid),
    ).length
    return selectedCount > 0 && selectedCount < rows.length
  }

  const checkboxStateChecked = (rows: Row<T>[]) => {
    return (
      rows.length > 0 &&
      rows.every((r) => selectedAllPages.has(r.original.guid))
    )
  }

  const isSelected = (row: Row<T>) => {
    return (
      selectedAllPages.has(row.original.guid) &&
      (checkedRows ? checkedRows.includes(row.original.guid) : true)
    )
  }

  // Initialize selectedByPage when data changes
  // Ensure we always have at least one page
  useEffect(() => {
    if (pageSize) {
      const pageCount = Math.max(
        1,
        dataLength ? Math.ceil(dataLength / pageSize) : 1,
      )
      const initialSelectedByPage = Array.from(
        { length: pageCount },
        () => new Set<string>(),
      )
      // Only update if the length changed
      if (selectedByPage.length !== pageCount) {
        setSelectedByPage(initialSelectedByPage)
      }
    }
  }, [dataLength, pageSize, selectedByPage.length])

  return {
    selected,
    setSelected,
    selectedAllPages,
    setSelectedAllPages,
    handleClick,
    handleSelectAllClick,
    checkboxStateIndeterminate,
    checkboxStateChecked,
    isSelected,
  }
}
